// Free Shipping + Free Gift Bar
var FreeShippingBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["threshold", "total-price", "gift-threshold"];
  }

  constructor() {
    super();
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: "currency",
      currency: Shopify.currency.active
    });
    this.totalPrice = 0;
  }

  async connectedCallback() {
    // Normalize thresholds to current currency
    this.threshold = Math.round(this.getAttribute("threshold") * (Shopify.currency.rate || 1));
    this.giftThreshold = Math.round(this.getAttribute("gift-threshold") * (Shopify.currency.rate || 1));

    document.addEventListener("theme:cart:update", this._onCartChangedListener);
    document.addEventListener("theme:cart:change", this._onCartChangedListener);

    // Fetch initial cart data on load
    await this.fetchAndUpdateCart();

    this._updateMessage();
  }

  disconnectedCallback() {
    document.removeEventListener("theme:cart:update", this._onCartChangedListener);
    document.removeEventListener("theme:cart:change", this._onCartChangedListener);
  }

  get threshold() {
    return parseFloat(this.getAttribute("threshold"));
  }
  set threshold(value) {
    this.setAttribute("threshold", value);
  }

  get giftThreshold() {
    return parseFloat(this.getAttribute("gift-threshold"));
  }
  set giftThreshold(value) {
    this.setAttribute("gift-threshold", value);
  }

  get totalPrice() {
    return parseFloat(this.getAttribute("total-price"));
  }
  set totalPrice(value) {
    this.setAttribute("total-price", value);
  }

  async attributeChangedCallback(attribute, oldValue, newValue) {
    await window.customElements.whenDefined("progress-bar");
    const progressBarElement = this.querySelector("progress-bar");

    if (attribute === "threshold") {
      progressBarElement.valueMax = this.giftThreshold;
    }

    if (attribute === "total-price") {
      const numericValue = parseFloat(newValue) || 0;
      progressBarElement.setAttribute("aria-valuenow", numericValue);

      const valMax = parseFloat(progressBarElement.getAttribute("aria-valuemax")) || this.giftThreshold || this.threshold;
      const ratio = valMax > 0 ? Math.min(1, numericValue / valMax) : 0;
      progressBarElement.style.setProperty("--progress", ratio);
      
      this._updateMessage();
    }
  }

  async fetchAndUpdateCart() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      this._processCartData(cartData);
      this._updateMessage();
    } catch (error) {
      console.error("Error fetching cart data:", error);
      this._updateMessage();
    }
  }

  _processCartData(cart) {
    // Try to get subtotal from DOM first
    const subtotalElement = document.querySelector('#subtotal-amount, [data-subtotal-amount], .cart-drawer__subtotal .price, dd.price');
    
    if (subtotalElement) {
      // Extract number from text like "$137.97 USD"
      const subtotalText = subtotalElement.textContent.trim();
      const numberMatch = subtotalText.match(/[\d,]+\.?\d*/);
      
      if (numberMatch) {
        const subtotalValue = parseFloat(numberMatch[0].replace(/,/g, ''));
        this.totalPrice = subtotalValue * 100; // Convert to cents
        console.log('Subtotal from DOM:', subtotalValue);
      } else {
        // Fallback to cart.js data
        this.totalPrice = cart.total_price;
        console.log('Using cart.total_price:', cart.total_price / 100);
      }
    } else {
      // Fallback to cart.js data
      this.totalPrice = cart.total_price;
      console.log('Using cart.total_price (no DOM element):', cart.total_price / 100);
    }
    
    console.log('Threshold:', this.threshold / 100);
    console.log('Gift Threshold:', this.giftThreshold / 100);
    console.log('Total Price (cents):', this.totalPrice);
    
    const progressBarElement = this.querySelector('progress-bar');
    if (progressBarElement) {
      progressBarElement.valueNow = this.totalPrice;
    }
  }

  _updateMessage() {
    const currency = this._currencyFormatter;
    const msgEl = this.querySelector(".progress-message");

    const currentThreshold = parseFloat(this.threshold);
    const currentGiftThreshold = parseFloat(this.giftThreshold);
    const currentTotalPrice = parseFloat(this.totalPrice);

    const remainingShipping = currentThreshold - currentTotalPrice;
    const remainingGift = currentGiftThreshold - currentTotalPrice;

    const replacements = {
      "{shipping}": currency.format(Math.max(0, remainingShipping) / 100),
      "{gift}": currency.format(Math.max(0, remainingGift) / 100)
    };

    let message = "";

    if (currentTotalPrice >= currentGiftThreshold) {
      message = this.getAttribute("msg-gift");
    } else if (currentTotalPrice >= currentThreshold) {
      message = this.getAttribute("msg-shipping-unlocked");
    } else {
      message = this.getAttribute("msg-below-shipping");
    }

    Object.keys(replacements).forEach(key => {
      message = message.replaceAll(key, replacements[key]);
    });

    if (msgEl) {
      msgEl.innerHTML = message;
    }
    
    this._updateMarkers();
  }

  _updateMarkers() {
    const shippingMarker = this.querySelector(".shipping-marker");
    const giftMarker = this.querySelector(".gift-marker");
    
    const currentTotalPrice = parseFloat(this.totalPrice) || 0;
    
    if (shippingMarker) {
      if (currentTotalPrice >= this.threshold) {
        shippingMarker.classList.add("reached");
      } else {
        shippingMarker.classList.remove("reached");
      }
    }
    
    if (giftMarker) {
      if (currentTotalPrice >= this.giftThreshold) {
        giftMarker.classList.add("reached");
      } else {
        giftMarker.classList.remove("reached");
      }
    }
  }

  async _onCartChanged(event) {
    await this.fetchAndUpdateCart();
  }
};

if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// Progress bar component
var ProgressBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  
  attributeChangedCallback() {
    const valNow = parseFloat(this.getAttribute("aria-valuenow"));
    const valMax = parseFloat(this.getAttribute("aria-valuemax"));

    if (!isNaN(valNow) && !isNaN(valMax) && valMax > 0) {
      this.style.setProperty(
        "--progress",
        `${Math.min(1, valNow / valMax)}`
      );
    } else {
      this.style.setProperty("--progress", "0");
    }
  }
};

if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}